/**
 * SettingsContext
 * Global display & filter settings persisted to localStorage.
 * These settings genuinely affect what data is shown throughout the app.
 */
import React, { createContext, useContext, useState, useCallback } from 'react'

const STORAGE_KEY = 'sf_settings'

const defaults = {
  // Signal display filters (applied to all signal lists)
  minStrength: 0,          // 0–1
  minConfidence: 0,        // 0–1
  directionFilter: 'ALL',  // 'ALL' | 'BUY' | 'SELL'
  // signalTypes removed 2026-11-04 (T-365): the toggles named retired signal types and never filtered anything live.


  // Data fetch window
  signalHours: 24,         // how many hours back to fetch signals

  // Display
  refreshInterval: 30,     // seconds between auto-refresh
  defaultSymbol: 'SPY',    // default symbol for analysis page
}

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return { ...defaults, ...JSON.parse(raw) }
  } catch {}
  return defaults
}

const SettingsContext = createContext(null)

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(load)

  const update = useCallback((patch) => {
    setSettings(prev => {
      const next = { ...prev, ...patch }
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)) } catch {}
      return next
    })
  }, [])

  const reset = useCallback(() => {
    setSettings(defaults)
    try { localStorage.removeItem(STORAGE_KEY) } catch {}
  }, [])

  /** Returns true if a signal object passes the current filters. */
  const passesFilter = useCallback((signal) => {
    const s = settings
    if (Number(signal.strength) < s.minStrength) return false
    if (Number(signal.confidence) < s.minConfidence) return false
    if (s.directionFilter !== 'ALL') {
      const dir = (signal.direction || '').toUpperCase()
      if (dir !== s.directionFilter) return false
    }
    return true
  }, [settings])

  return (
    <SettingsContext.Provider value={{ settings, update, reset, passesFilter }}>
      {children}
    </SettingsContext.Provider>
  )
}

export function useSettings() {
  const ctx = useContext(SettingsContext)
  if (!ctx) throw new Error('useSettings must be used inside SettingsProvider')
  return ctx
}
